/**
 * Payment service for handling subscriptions and credit purchases
 */
import api from './api';

export interface CreditPackage {
  id: string;
  name: string;
  description?: string;
  credits: number;
  bonus_credits: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
  stripe_price_id?: string;
  stripe_product_id?: string;
  created_at: string;
  updated_at: string;
  price_dollars: number;
  total_credits: number;
  credits_per_dollar: number;
}

export interface Subscription {
  id: string;
  user_id: string;
  tier_id: string;
  stripe_subscription_id: string;
  stripe_price_id?: string;
  status: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  trial_start?: string;
  trial_end?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  is_trial: boolean;
  days_until_renewal?: number;
}

export interface PaymentTransaction {
  id: string;
  user_id: string;
  stripe_payment_intent_id?: string;
  stripe_charge_id?: string;
  stripe_invoice_id?: string;
  amount_cents: number;
  currency: string;
  status: string;
  payment_type: string;
  description?: string;
  metadata?: Record<string, any>;
  refunded_amount_cents?: number;
  refund_reason?: string;
  created_at: string;
  updated_at: string;
  amount_dollars: number;
  refunded_amount_dollars: number;
  net_amount_cents: number;
  net_amount_dollars: number;
}

export interface BillingSummary {
  current_subscription?: Subscription;
  payment_method?: Record<string, any>;
  upcoming_invoice?: Record<string, any>;
  recent_transactions: PaymentTransaction[];
  total_spent: number;
  credits_purchased: number;
}

export interface UsageStats {
  current_period_start?: string;
  current_period_end?: string;
  credits_used: number;
  credits_remaining: number;
  features_used: Record<string, number>;
}

export interface CreateCheckoutSessionRequest {
  tier_id: string;
  billing_period: 'monthly' | 'yearly';
  success_url?: string;
  cancel_url?: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface PurchaseCreditsRequest {
  package_id: string;
  success_url?: string;
  cancel_url?: string;
}

export interface PurchaseCreditsResponse {
  checkout_url: string;
  session_id: string;
}

export interface CustomerPortalRequest {
  return_url?: string;
}

export interface CustomerPortalResponse {
  portal_url: string;
}

class PaymentService {
  /**
   * Create checkout session for subscription
   */
  async createSubscriptionCheckout(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    console.log('🔐 Making subscription API call with request:', request);
    
    try {
      const response = await api.post('/payments/subscribe', request);
      console.log('✅ Subscription API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🚨 Subscription API Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        request: request
      });
      throw error;
    }
  }

  /**
   * Create checkout session for credit purchase
   */
  async purchaseCreditsCheckout(request: PurchaseCreditsRequest): Promise<PurchaseCreditsResponse> {
    const response = await api.post('/payments/purchase-credits', request);
    return response.data;
  }

  /**
   * Create customer portal session
   */
  async createCustomerPortalSession(request: CustomerPortalRequest = {}): Promise<CustomerPortalResponse> {
    const response = await api.post('/payments/customer-portal', request);
    return response.data;
  }

  /**
   * Get available credit packages
   */
  async getCreditPackages(): Promise<CreditPackage[]> {
    const response = await api.get('/payments/credit-packages');
    return response.data;
  }

  /**
   * Get current subscription
   */
  async getCurrentSubscription(): Promise<Subscription> {
    const response = await api.get('/payments/subscription');
    return response.data;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(atPeriodEnd: boolean = true): Promise<Subscription> {
    const response = await api.post(`/payments/subscription/cancel?at_period_end=${atPeriodEnd}`);
    return response.data;
  }

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(): Promise<Subscription> {
    const response = await api.post('/payments/subscription/reactivate');
    return response.data;
  }

  /**
   * Downgrade to a lower tier
   */
  async downgradeTier(targetTierId: string): Promise<{ success: boolean; message: string; new_tier: string; effective_date: string }> {
    const response = await api.post(`/payments/subscription/downgrade-tier?target_tier_id=${targetTierId}`);
    return response.data;
  }

  /**
   * Get billing summary
   */
  async getBillingSummary(): Promise<BillingSummary> {
    const response = await api.get('/payments/billing-summary');
    return response.data;
  }

  /**
   * Get usage statistics
   */
  async getUsageStats(): Promise<UsageStats> {
    const response = await api.get('/payments/usage-stats');
    return response.data;
  }

  /**
   * Get payment transaction history
   */
  async getPaymentTransactions(limit: number = 20): Promise<PaymentTransaction[]> {
    const response = await api.get(`/payments/transactions?limit=${limit}`);
    return response.data;
  }

  /**
   * Subscribe to a tier (creates checkout session and redirects)
   */
  async subscribeToTier(tierId: string, billingPeriod: 'monthly' | 'yearly' = 'monthly'): Promise<void> {
    const response = await this.createSubscriptionCheckout({
      tier_id: tierId,
      billing_period: billingPeriod,
      success_url: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/pricing`
    });
    
    // Redirect to Stripe Checkout
    window.location.href = response.checkout_url;
  }

  /**
   * Purchase credits (creates checkout session and redirects)
   */
  async purchaseCredits(packageId: string): Promise<void> {
    const response = await this.purchaseCreditsCheckout({
      package_id: packageId,
      success_url: `${window.location.origin}/credits/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/app/billing`
    });
    
    // Redirect to Stripe Checkout
    window.location.href = response.checkout_url;
  }

  /**
   * Open customer portal (redirects to Stripe portal)
   */
  async openCustomerPortal(): Promise<void> {
    const response = await this.createCustomerPortalSession({
      return_url: `${window.location.origin}/billing`
    });
    
    // Redirect to Stripe Customer Portal
    window.location.href = response.portal_url;
  }

  /**
   * Get scheduled tier change
   */
  async getScheduledTierChange(): Promise<any> {
    const response = await api.get('/payments/subscription/scheduled-change');
    return response.data;
  }

  /**
   * Cancel scheduled tier change
   */
  async cancelScheduledTierChange(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/payments/subscription/cancel-scheduled-downgrade');
    return response.data;
  }
}

export default new PaymentService();